import { useEffect, useRef, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  AddUpdateOfficeReference,
  DeleteOfficeReference,
  getBothCourtCasesDetailsByCourtRef,
  getOfficeReferenceByCourtRef,
  GetReAppealedCasesByCourtRef,
  EditCaseAssignmentByCourtRef,
  DeleteCaseAssignmentById,
  AddOrUpdateReAppeal,
  DeleteReAppealById,
  getCaseTypePIndex,
  addDcNextHearingDate,
  addHcNextHearingDate,
  addOcNextHearingDate,
  UpdateHighManualCase,
  UpdateDistrictManualCase,
  UpdateOtherManualCase,
  UpdateSupremeCourtManualCase,
} from "../../services/api/CasesApi.js";
import { getCourtTypeByCourtRef } from "../../services/api/MyClientsApi.js";
import "./CaseDetails.scss";
import "../../components/Loading/Loading.scss";
import TabViewer from "../../components/TabViewer/TabViewer";
import { WrittenFormat } from "../../components/WrittenFormat.js";
import { getLabelByCourtRef } from "../../services/api/LabelApi.js";
import {
  getCaseAssignmentToUserByCourtRef,
  getUserDataByID,
} from "../../services/api/AuthApi.js";
import { getTimelineRemarksByCourtRef } from "../../services/api/TabViewerApi.js";
import { getHierarchy } from "../../services/api/HierarchyApi.js";
import { useToast } from "../../context/ToastContext";
import { useLoading } from "../../context/LoadingContext";
import { FiEdit, FiSave, FiTrash2, FiPlus, FiX, FiDownload } from "react-icons/fi";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal.js";
import AssignmentModal from "../../components/AssignmentModal/AssignmentModal.js";
import {
  formatText,
  normalizePartyNames,
  truncatePartyNames,
} from "../../components/TruncateParties.js";
import { useAuth } from "../../context/AuthContext";
import gsap from "gsap";
import LabelSelectionModal from "../../components/LabelSelectionModal/LabelSelectionModal.js";
import { LabelForm } from "../../forms/LabelForm/LabelForm.js";
import { useCasePagination } from "../../context/CasePaginationContext.js";
import { districtCourtMapping } from "../../constants/districtCourtMap.js";
import { getClientsByCases } from "../../services/api/CasesApi.js";
import { getAllGlobalSettingsData } from "../../services/api/GlobalSettingsApi.js";
import { exportCasesToPDF } from "../../components/exportPdf.js";

function parseClientParties(clients) {
  return clients.flatMap(client => {
    const parts = client.name.split(/,?\s*\d+\s+/).map(s => s.trim()).filter(Boolean);
    return parts.map((name, i) => ({ key: `${client._id}_${i}`, clientId: client._id, name }));
  });
}

function parseAdvocates(advocatesStr) {
  if (!advocatesStr) return [];
  return advocatesStr
    .split(/(?:\s*\[[^\]]+\])+/)
    .map(s => s.replace(/,\s*$/, '').trim())
    .filter(Boolean);
}

export default function CaseDetails() {
  const { courtref } = useParams();
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  const location = useLocation();
  const selectedCourtType = location.state?.selectedCourtType;
  const selectedCaseType = location.state?.selectedCaseType;

  const { paginationData, updatePaginationData, resetPaginationData } = useCasePagination();

  const [caseData, setCaseData] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const [casedetaillabels, setCaseDetailLabels] = useState([]);
  const [fetchedRefs, setFetchedRefs] = useState(new Set());

  const [assignedUsers, setAssignedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [clients, setClients] = useState([]);

  const [showAllClients, setShowAllClients] = useState(false);
  const [showAllAdvocates, setShowAllAdvocates] = useState(false);
  const [showLabelSelectionModal, setShowLabelSelectionModal] = useState(false);

  // Assignment modal state
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [assignmentModalMode, setAssignmentModalMode] = useState("assign"); // "assign", "edit", "delete"

  const [officeReference, setOfficeReference] = useState(null);
  const [officeRefInput, setOfficeRefInput] = useState("");
  const [isEditingOfficeRef, setIsEditingOfficeRef] = useState(false);

  // Status Edit State
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [statusInput, setStatusInput] = useState(false); // false = Pending, true = Disposed
  const [decisionDateInput, setDecisionDateInput] = useState("");

  // Add re-appeal state
  const [reAppealData, setReAppealData] = useState(null);
  const [appealPeriodDaysInput, setAppealPeriodDaysInput] = useState("");
  const [appealDeadlineDateInput, setAppealDeadlineDateInput] = useState("");
  const [isEditingAppeal, setIsEditingAppeal] = useState(false);
  const [showReAppealModal, setShowReAppealModal] = useState(false);

  const [showLabelFormModal, setShowLabelFormModal] = useState(false);

  // Next Hearing Date state
  const [isEditingNextHearingDate, setIsEditingNextHearingDate] = useState(false);
  const [nextHearingDateInput, setNextHearingDateInput] = useState("");

  const appealPeriodInputRef = useRef(null);
  const officeRefInputRef = useRef(null);
  const nextHearingDateInputRef = useRef(null);
  const [globalSettingsRaw, setGlobalSettingsRaw] = useState(null);

  const { role } = useAuth();
  const [caseSettings, setCaseSettings] = useState(null);

  const getOrdinalSuffix = (day) => {
    const j = day % 10;
    const k = day % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };

  const parseFancyDate = (dateStr) => {
    if (!dateStr) return null;

    try {
      // Remove ordinal suffixes (st, nd, rd, th)
      const cleanedDate = dateStr.replace(/(\d+)(st|nd|rd|th)/, "$1");
      const parsed = new Date(cleanedDate);

      // Check if date is valid
      if (isNaN(parsed.getTime())) return null;

      return parsed;
    } catch (error) {
      return null;
    }
  };
  const { showToast } = useToast();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    itemId: null,
    itemType: "",
    userName: "",
  });

  const [isPartyNamesExpanded, setIsPartyNamesExpanded] = useState(false);
  const partyNamesContentRef = useRef(null);
  const partyNamesContainerRef = useRef(null);
  // ... existing hooks

  // --- LOGIC TO RESOLVE STATE & DISTRICT NAMES ---
  let resolvedState = "N/A";
  let resolvedDistrict = "N/A";

  if (caseData?.case) {
    const cObj = caseData.case;

    // Check if it's a District Court Case (has stateCourtComplex)
    if (cObj.stateCourtComplex) {
      // --- DC LOGIC (Use Codes) ---
      const stateCode = cObj.state;          // e.g., "17"
      const districtCode = cObj.stateDistrict; // e.g., "15"

      // 1. Resolve State
      resolvedState = districtCourtMapping?.states?.[stateCode]?.name || stateCode || "N/A";

      // 2. Resolve District (Requires valid State Code first)
      resolvedDistrict = districtCourtMapping?.states?.[stateCode]?.districts?.[districtCode]?.name || districtCode || "N/A";

    } else {
      // --- HC LOGIC (Use Text directly from caseStatus) ---
      resolvedState = cObj.caseStatus?.["State"] || "N/A";
      resolvedDistrict = cObj.caseStatus?.["District"] || "N/A";
    }
  }
  // ------------------------------------------------

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getAllGlobalSettingsData();
        if (res?.data) {
          setGlobalSettingsRaw(res.data);
        }
        if (res?.data?.result?.[0]?.cases) {
          setCaseSettings(res.data.result[0].cases);
        }
      } catch (err) {
        console.error("Error fetching case settings:", err);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (!partyNamesContentRef.current || !partyNamesContainerRef.current)
      return;

    const content = partyNamesContentRef.current;
    const container = partyNamesContainerRef.current;

    if (isPartyNamesExpanded) {
      // Expanding animation
      gsap.fromTo(
        container,
        { height: "auto", overflow: "hidden" },
        { duration: 0.5, ease: "power2.out" }
      );
      gsap.fromTo(
        content,
        { opacity: 0.7, y: -10 },
        { opacity: 1, y: 0, duration: 0.3, delay: 0.1, ease: "power2.out" }
      );
    } else {
      // Collapsing animation
      gsap.to(content, {
        opacity: 0.7,
        y: -5,
        duration: 0.2,
        ease: "power2.in",
      });
      gsap.to(container, {
        height: "auto",
        duration: 0.4,
        ease: "power2.in",
        delay: 0.1,
      });
    }
  }, [isPartyNamesExpanded]);

  const partyNames = caseData?.case?.parties || "Parties Name Here";
  const truncatedPartyNames = truncatePartyNames(partyNames);
  const fullPartyNames = normalizePartyNames(partyNames);
  const needsPartyTruncation = truncatedPartyNames !== partyNames;

  const refreshCaseDetailLabels = async () => {
    if (!courtref) return;

    try {
      const res = await getLabelByCourtRef(courtref);
      setCaseDetailLabels(res.data);
    } catch (err) {
      console.error("Error refreshing case detail labels:", err);
      setCaseDetailLabels([]);
    }
  };

  const hasCaseData = (data) => {
    if (!data || !data.case) return false;

    const hasBasicInfo =
      data.case.parties ||
      data.case.caseDetails?.["CNR Number"] ||
      data.case.caseDetails?.["Filing Number"] ||
      data.case.caseStatus?.["Case Status"];

    return hasBasicInfo;
  };

  const refreshOfficeReference = async () => {
    try {
      const response = await getOfficeReferenceByCourtRef(courtref);
      const officeRef = response.data?.caseAssignment;
      if (officeRef) {
        setOfficeReference(officeRef);
        setOfficeRefInput(officeRef.officeRefNo || "");
      } else {
        setOfficeReference(null);
        setOfficeRefInput("");
      }
    } catch (err) {
      console.error("Error refreshing office reference:", err);
    }
  };

  const refreshReAppealData = async () => {
    try {
      const response = await GetReAppealedCasesByCourtRef(courtref);
      if (response?.data && Object.keys(response.data).length > 0) {
        setReAppealData(response.data);
        setAppealPeriodDaysInput(
          response.data.appealPeriodDays?.toString() || ""
        );
        setAppealDeadlineDateInput(response.data.appealDeadlineDate || "");
      } else {
        setReAppealData(null);
        setAppealPeriodDaysInput("");
        setAppealDeadlineDateInput("");
      }
    } catch (err) {
      console.error("Error refreshing re-appeal data:", err);
      setReAppealData(null);
      setAppealPeriodDaysInput("");
      setAppealDeadlineDateInput("");
    }
  };

  const refreshAssignments = async () => {
    try {
      const response = await getCaseAssignmentToUserByCourtRef(courtref);
      const assignments = response.data?.caseAssignment || [];

      const userPromises = assignments.map((assignment) =>
        getUserDataByID(assignment.user)
          .then((userRes) => ({
            id: assignment.user,
            assignmentId: assignment._id,
            name: userRes.data?.name || "Unknown User",
          }))
          .catch(() => ({
            id: assignment.user,
            assignmentId: assignment._id,
            name: "Unknown User",
          }))
      );

      const userDetails = await Promise.all(userPromises);
      setAssignedUsers(userDetails);
    } catch (err) {
      console.error("Error refreshing assignments:", err);
      setAssignedUsers([]);
    }
  };

  useEffect(() => {
    if (!courtref) return;

    getOfficeReferenceByCourtRef(courtref)
      .then((res) => {
        const officeRef = res.data?.caseAssignment;
        if (officeRef) {
          setOfficeReference(officeRef);
          setOfficeRefInput(officeRef.officeRefNo || "");
        }
      })
      .catch((err) => {
        console.error("Error fetching office reference:", err);
      });
  }, [courtref]);

  useEffect(() => {
    getHierarchy()
      .then((res) => {
        const users = res.data?.users || [];
        const activeUsers = users.filter((user) => user.isActive !== false);
        setAvailableUsers(activeUsers);
      })
      .catch((err) => {
        console.error("Error fetching available users:", err);
        setAvailableUsers([]);
      });
  }, []);

  useEffect(() => {
    if (!courtref) return;

    GetReAppealedCasesByCourtRef(courtref)
      .then((res) => {
        if (res?.data && Object.keys(res.data).length > 0) {
          setReAppealData(res.data);
          setAppealPeriodDaysInput(res.data.appealPeriodDays?.toString() || "");
          setAppealDeadlineDateInput(res.data.appealDeadlineDate || "");
        } else {
          setReAppealData(null);
          setAppealPeriodDaysInput("");
          setAppealDeadlineDateInput("");
        }
      })
      .catch((err) => {
        console.error("Error fetching re-appeal data:", err);
        setReAppealData(null);
        setAppealPeriodDaysInput("");
        setAppealDeadlineDateInput("");
      });
  }, [courtref]);

  const handleSaveOfficeRef = async () => {
    if (!officeRefInput.trim()) {
      showToast("Please enter a reference number", "error");
      return;
    }

    try {
      const payload = {
        officeRefNo: officeRefInput.trim(),
        courtRef: courtref,
      };

      await AddUpdateOfficeReference(payload);
      await refreshOfficeReference();

      setIsEditingOfficeRef(false);
      showToast(
        officeReference
          ? "Office reference updated successfully!"
          : "Office reference added successfully!",
        "success"
      );
    } catch (error) {
      console.error("Error saving office reference:", error);
      showToast("Failed to save office reference. Please try again.", "error");
    }
  };

  const handleSaveAppeal = async () => {
    if (!appealPeriodDaysInput.trim()) {
      showToast("Please enter appeal period days", "error");
      return;
    }

    const appealPeriodDays = parseInt(appealPeriodDaysInput.trim());
    if (isNaN(appealPeriodDays) || appealPeriodDays <= 0) {
      showToast("Please enter a valid number for appeal period days", "error");
      return;
    }

    // Auto-calculate deadline based on decision date and appeal period
    const decisionDate = caseData?.case.caseStatus?.["Decision Date"];
    const calculatedDeadline = calculateAppealDeadline(
      decisionDate,
      appealPeriodDays
    );

    if (!calculatedDeadline && !appealDeadlineDateInput.trim()) {
      showToast(
        "Could not calculate deadline automatically. Please enter deadline date manually.",
        "error"
      );
      return;
    }

    try {
      const payload = {
        courtRef: courtref,
        appealPeriodDays: appealPeriodDays,
        appealDeadlineDate:
          calculatedDeadline || appealDeadlineDateInput.trim(),
      };

      await AddOrUpdateReAppeal(payload);
      await refreshReAppealData();

      setIsEditingAppeal(false);
      showToast(
        reAppealData
          ? "Appeal information updated successfully!"
          : "Appeal information added successfully!",
        "success"
      );
    } catch (error) {
      console.error("Error saving appeal information:", error);
      showToast(
        "Failed to save appeal information. Please try again.",
        "error"
      );
    }
  };

  const handleAppealPeriodChange = (e) => {
    const value = e.target.value;
    setAppealPeriodDaysInput(value);

    // Auto-calculate deadline when appeal period changes
    if (value && !isNaN(parseInt(value))) {
      const decisionDate = caseData?.case.caseStatus?.["Decision Date"];
      const calculatedDeadline = calculateAppealDeadline(
        decisionDate,
        parseInt(value)
      );
      if (calculatedDeadline) {
        setAppealDeadlineDateInput(calculatedDeadline);
      }
    }
  };
  const handleOpenLabelModal = () => {
    setShowLabelSelectionModal(true);
  };

  const handleCloseLabelModal = () => {
    setShowLabelSelectionModal(false);
  };
  const handleOpenLabelForm = () => {
    setShowLabelFormModal(true);
  };

  // Add new handler for closing label form
  const handleCloseLabelForm = () => {
    setShowLabelFormModal(false);
  };

  const handleLabelAdded = async () => {
    // Refresh the case detail labels after adding
    await refreshCaseDetailLabels();
  };
  const handleLabelFormSuccess = async () => {
    setShowLabelFormModal(false);
    await refreshCaseDetailLabels();
    showToast("Label created and assigned successfully!", "success");
  };
  const handleDeleteOfficeRef = () => {
    setDeleteModal({
      isOpen: true,
      itemId: officeReference._id,
      itemType: "office reference",
      userName: "",
    });
  };

  const handleDeleteAppeal = () => {
    setDeleteModal({
      isOpen: true,
      itemId: reAppealData._id,
      itemType: "appeal information",
      userName: "",
    });
  };

  // New modal handlers
  const handleOpenAssignmentModal = (mode = "assign") => {
    setAssignmentModalMode(mode);
    setIsAssignmentModalOpen(true);
  };

  const handleCloseAssignmentModal = () => {
    setIsAssignmentModalOpen(false);
    setAssignmentModalMode("assign");
  };

  const handleModalSaveAssignments = async (selectedUserIds) => {
    try {
      const payload = {
        courtRef: [courtref],
        users: selectedUserIds,
      };

      await EditCaseAssignmentByCourtRef(payload);
      await refreshAssignments();
      showToast("Case assignments updated successfully!", "success");
    } catch (error) {
      console.error("Error updating assignments:", error);
      showToast("Failed to update assignments. Please try again.", "error");
    }
  };

  const handleModalDeleteUser = (assignmentId, userName) => {
    setDeleteModal({
      isOpen: true,
      itemId: assignmentId,
      itemType: "individual assignment",
      userName: userName,
    });
  };

  const handleModalDeleteAll = () => {
    setDeleteModal({
      isOpen: true,
      itemId: courtref,
      itemType: "all assignments",
      userName: "",
    });
  };

  const confirmDeleteOfficeRef = async () => {
    setDeleteModal({ isOpen: false, itemId: null, itemType: "", userName: "" });

    try {
      await DeleteOfficeReference(officeReference._id);
      await refreshOfficeReference();

      setIsEditingOfficeRef(false);
      showToast("Office reference deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting office reference:", error);
      showToast(
        "Failed to delete office reference. Please try again.",
        "error"
      );
    }
  };

  const confirmDeleteAppeal = async () => {
    setDeleteModal({ isOpen: false, itemId: null, itemType: "", userName: "" });

    try {
      await DeleteReAppealById(reAppealData._id);
      await refreshReAppealData();

      setIsEditingAppeal(false);
      showToast("Appeal information deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting appeal information:", error);
      showToast(
        "Failed to delete appeal information. Please try again.",
        "error"
      );
    }
  };

  const confirmDeleteAssignments = async () => {
    const { itemId, itemType } = deleteModal;
    setDeleteModal({ isOpen: false, itemId: null, itemType: "", userName: "" });

    try {
      if (itemType === "individual assignment") {
        await DeleteCaseAssignmentById(itemId);
        await refreshAssignments();
        showToast("Assignment deleted successfully!", "success");
      } else {
        const assignmentPromises = assignedUsers.map((user) =>
          DeleteCaseAssignmentById(user.assignmentId)
        );
        await Promise.all(assignmentPromises);
        await refreshAssignments();
        showToast("All case assignments deleted successfully!", "success");
      }
    } catch (error) {
      console.error("Error deleting assignments:", error);
      showToast("Failed to delete assignments. Please try again.", "error");
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, itemId: null, itemType: "", userName: "" });
  };

  const handleEditOfficeRef = () => {
    setIsEditingOfficeRef(true);
  };

  const handleEditAppeal = () => {
    setIsEditingAppeal(true);
  };

  const handleOpenReAppealModal = () => {
    setShowReAppealModal(true);
  };

  const handleCloseReAppealModal = () => {
    setShowReAppealModal(false);
  };

  const handleReAppealSubmit = async (reAppealSubmitData) => {
    try {
      const payload = {
        courtRef: courtref,
        appealPeriodDays: reAppealSubmitData.appealPeriodDays,
        appealDeadlineDate: reAppealSubmitData.appealDeadlineDate,
      };


      const response = await AddOrUpdateReAppeal(payload);

      await refreshReAppealData();

      showToast(
        reAppealData
          ? "Re-appeal data updated successfully!"
          : "Re-appeal data added successfully!",
        "success"
      );
    } catch (error) {
      console.error("Error saving re-appeal data:", error);
      showToast("Failed to save re-appeal data. Please try again.", "error");
      throw error;
    }
  };

  const getCaseTypeParam = (source, caseType) => {
    if (!source || !caseType) {
      return null;
    }

    const sourceType = source.toLowerCase().trim();
    const selectedType = caseType.toLowerCase().trim();


    // High Court - use includes() to catch any variation
    if (sourceType.includes('high')) {
      const result = selectedType === 'fetched' ? 'hc-fetched' : 'hc-added';
      return result;
    }

    // District Court - use includes() to catch any variation
    if (sourceType.includes('district')) {
      const result = selectedType === 'fetched' ? 'dc-fetched' : 'dc-added';
      return result;
    }

    // Other Court
    if (sourceType.includes('other')) {
      return 'oc-added';
    }

    // Supreme Court
    if (sourceType.includes('supreme')) {
      return selectedType === 'fetched' ? 'sc-fetched' : 'sc-added';
    }

    console.warn('No matching court type found for source:', source);
    return null;
  };

  const [paginationFetched, setPaginationFetched] = useState(false);

  const fetchPaginationData = async (source, caseType, currentCourtRef, forceFetch = false) => {
    const caseTypeParam = getCaseTypeParam(source, caseType);

    if (!caseTypeParam) {
      console.error('Unable to determine case type parameter', { source, caseType });
      return;
    }

    // If not forcing fetch and we have data for same case type, just update index
    if (!forceFetch &&
      paginationData.caseType === caseTypeParam &&
      paginationData.courtRefs.length > 0) {
      const currentIndex = paginationData.courtRefs.findIndex(ref => ref === currentCourtRef);
      if (currentIndex !== -1) {
        updatePaginationData({ currentIndex });
      }
      return;
    }

    try {
      updatePaginationData({ isLoading: true });

      const response = await getCaseTypePIndex(caseTypeParam);

      const { courtRefs, totalcases } = response.data;


      if (!courtRefs || courtRefs.length === 0) {
        console.warn('No court refs returned from API');
      }

      const currentIndex = courtRefs.findIndex(ref => ref === currentCourtRef);

      // Check if courtRef exists in array
      if (currentIndex === -1) {
        console.error('CURRENT COURTREF NOT FOUND IN ARRAY!');
      } else {
        console.log('Current courtRef found at index:');
      }

      updatePaginationData({
        courtRefs,
        totalCases: totalcases,
        currentIndex,
        caseType: caseTypeParam,
        isLoading: false,
      });

      setPaginationFetched(true);
    } catch (error) {
      console.error('Error fetching pagination data:', error);
      updatePaginationData({ isLoading: false });
    }
  };


  useEffect(() => {
    if (isEditingOfficeRef && officeRefInputRef.current) {
      setTimeout(() => {
        try {
          officeRefInputRef.current.focus();
        } catch (e) { }
      }, 50);
    }
  }, [isEditingOfficeRef]);

  useEffect(() => {
    if (isEditingAppeal && appealPeriodInputRef.current) {
      setTimeout(() => {
        try {
          appealPeriodInputRef.current.focus();
          appealPeriodInputRef.current.select &&
            appealPeriodInputRef.current.select();
        } catch (e) {
          // ignore focus errors
        }
      }, 50);
    }
  }, [isEditingAppeal]);

  useEffect(() => {
    if (isEditingNextHearingDate && nextHearingDateInputRef.current) {
      setTimeout(() => {
        try {
          nextHearingDateInputRef.current.focus();
        } catch (e) {
          // ignore focus errors
        }
      }, 50);
    }
  }, [isEditingNextHearingDate]);

  const handleCancelEdit = () => {
    setIsEditingOfficeRef(false);
    setOfficeRefInput(officeReference?.officeRefNo || "");
  };

  const handleCancelEditAppeal = () => {
    setIsEditingAppeal(false);
    setAppealPeriodDaysInput(reAppealData?.appealPeriodDays?.toString() || "");
    setAppealDeadlineDateInput(reAppealData?.appealDeadlineDate || "");
  };

  const handleEditStatus = () => {
    setIsEditingStatus(true);
    const currentStatus = caseData?.case?.caseStatus?.["Case Status"] || caseData?.case?.caseStatus?.["Status"] || "Pending";
    const isDisposed = currentStatus.toLowerCase().includes("disposed");
    setStatusInput(isDisposed);

    const currentDecisionDate = caseData?.case?.caseStatus?.["Decision Date"] || "";
    const parsed = parseFancyDate(currentDecisionDate);
    if (parsed && !isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      setDecisionDateInput(`${year}-${month}-${day}`);
    } else {
      setDecisionDateInput("");
    }
  };

  const handleCancelEditStatus = () => {
    setIsEditingStatus(false);
    setStatusInput(false);
    setDecisionDateInput("");
  };

  const handleSaveStatus = async () => {
    try {
      const source = caseData?.source?.toLowerCase() || "";
      let payload = {};

      if (statusInput) { // Disposed
        if (!decisionDateInput) {
          showToast("Decision Date is required for Disposed cases", "error");
          return;
        }

        // Get the Next Hearing Date string
        const nextHearingDateStr = caseData?.case.caseStatus?.["Next Hearing Date"] || caseData?.case.customUndatedRows?.["Next Hearing Date"];
        const dateToCheck = nextHearingDateStr ? String(nextHearingDateStr).trim().toLowerCase() : "";
        console.log("Next Hearing Date to check:", dateToCheck);

        const isJunkDate = /^[\s:\-\."']+$/.test(dateToCheck);

        const isInvalidText = ["n/a", "na", "null", "undefined", ""].includes(dateToCheck);

        if (isJunkDate || isInvalidText) {
          console.log("Validation Failed: Date identified as invalid/placeholder.");
          showToast("Please add Next Hearing Date first", "error");
          return;
        }

        console.log("Validation Passed. Proceeding with disposal.");

        // Validate Decision Date against Next Hearing Date (Existing Logic)
        if (nextHearingDateStr) {
          const nextHearingDate = parseFancyDate(nextHearingDateStr);
          const decisionDate = new Date(decisionDateInput);

          if (nextHearingDate && !isNaN(nextHearingDate.getTime()) && !isNaN(decisionDate.getTime())) {
            // Normalize to date-only comparison (ignore time)
            const nhDateOnly = new Date(nextHearingDate.getFullYear(), nextHearingDate.getMonth(), nextHearingDate.getDate());
            const ddDateOnly = new Date(decisionDate.getFullYear(), decisionDate.getMonth(), decisionDate.getDate());

            if (ddDateOnly <= nhDateOnly) {
              showToast("Decision Date cannot be later than the Next Hearing Date.", "error");
              return;
            }
          }
        }

        // Format date to "1st October 2001"
        const dateObj = new Date(decisionDateInput);
        const day = dateObj.getDate();
        const monthNames = ["January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"];
        const month = monthNames[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
        const formattedDate = `${dayWithSuffix} ${month} ${year}`;

        const statusText = source.includes("district") ? "Case disposed" : "CASE DISPOSED";

        payload = {
          caseStatus: {
            ...caseData?.case?.caseStatus,
            "Case Status": statusText,
            "Decision Date": formattedDate
          }
        };
      } else { // Pending
        // Create a copy of caseStatus and exclude "Decision Date" and "Case Status"
        const { "Decision Date": _, "Case Status": __, ...restStatus } = caseData?.case?.caseStatus || {};
        payload = {
          caseStatus: {
            ...restStatus,
          }
        };
      }

      if (source.includes("high")) {
        await UpdateHighManualCase(courtref, payload);
      } else if (source.includes("district")) {
        await UpdateDistrictManualCase(courtref, payload);
      } else if (source.includes("supreme")) {
        await UpdateSupremeCourtManualCase(courtref, payload);
      } else {
        await UpdateOtherManualCase(courtref, payload);
      }

      // Refresh
      const res = await getBothCourtCasesDetailsByCourtRef(courtref);
      setCaseData(res?.data);
      setIsEditingStatus(false);
      showToast("Case status updated successfully!", "success");

    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Failed to update status", "error");
    }
  };
  // Add this function (e.g., before handleEditCase)
  const handleExportPDF = async () => {
    if (caseData) {
      try {
        showLoading("Preparing PDF export...");
        // Fetch remarks data
        const remarksResponse = await getTimelineRemarksByCourtRef(courtref);
        const remarksData = remarksResponse?.data || [];
        
        exportCasesToPDF([caseData], "portrait", globalSettingsRaw, true, remarksData);
        showToast("PDF exportede successfully!", "success");
      } catch (error) {
        console.error("Error exporting PDF:", error);
        // Fallback: export without remarks
        exportCasesToPDF([caseData], "portrait", globalSettingsRaw, true, []);
        showToast("PDF exported (remarks unavailable)", "warning");
      } finally {
        hideLoading();
      }
    }
  };

  const handleEditCase = async () => {
    try {
      showLoading("Loading form...");
      const courtTypeResponse = await getCourtTypeByCourtRef(courtref);
      const courtSource = courtTypeResponse?.data?.source;

      let courtType = "other"; // default

      if (courtSource) {
        // Map the API response source to court types
        switch (courtSource.toLowerCase()) {
          case "highcourt":
          case "high court":
            courtType = "high";
            break;
          case "districtcourt":
          case "district court":
          case "district":
            courtType = "district";
            break;
          case "othercourt":
          case "other court":
          case "other":
            courtType = "other";
            break;
          case "supremecourt":
          case "supreme court":
            courtType = "supreme";
            break;
          default:
            // If source contains 'high', it's high court
            if (courtSource.toLowerCase().includes("high")) {
              courtType = "high";
            } else if (courtSource.toLowerCase().includes("district")) {
              courtType = "district";
            } else if (courtSource.toLowerCase().includes("supreme")) {
              courtType = "supreme";
            } else {
              courtType = "other";
            }
        }
      }

      // Navigate to the edit form with the court type and court reference
      navigate(`/home/cases/editcase/${courtType}/${courtref}`);
    } catch (error) {
      console.error("Error determining court type for edit:", error);
      showToast("Failed to determine court type. Please try again.", "error");
    } finally {
      hideLoading();
    }
  };



  const isUndatedNextHearingDate = () => {
    const rawDate = (caseData?.case.caseStatus?.["Next Hearing Date"] ||
      caseData?.case.customUndatedRows?.["Next Hearing Date"] || "").toString().trim();

    // Missing date → not undated (no edit button)
    if (!rawDate || rawDate === "" || rawDate === "-" || rawDate === "--" ||
      rawDate === "---" || rawDate === "NA" || rawDate === "N/A" ||
      rawDate === ":" || rawDate === ":-") {
      return false;
    }

    // Parse the date — use parseAnyDate to support DD-MM-YYYY (SC) and ordinal formats (HC/DC)
    const parsed = parseAnyDate(rawDate);

    // Truly invalid → undated (show edit button)
    if (!parsed) {
      return true;
    }

    // Outdated (past) → undated (show edit button)
    const today = new Date();
    const parsedDateOnly = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Include only if parsed < today (yesterday or older)
    return parsedDateOnly < todayDateOnly;
  };

  const calculateAppealDeadline = (decisionDate, appealPeriodDays) => {
    if (!decisionDate || !appealPeriodDays) return "";

    try {
      // Parse the decision date (assuming format like "08th October 2021")
      const dateStr = decisionDate.replace(/(\d+)(st|nd|rd|th)/, "$1");
      const date = new Date(dateStr);

      if (isNaN(date.getTime())) return "";

      // Add appeal period days
      date.setDate(date.getDate() + parseInt(appealPeriodDays));

      // Format back to similar format
      const day = date.getDate();
      const suffix =
        day === 1 || day === 21 || day === 31
          ? "st"
          : day === 2 || day === 22
            ? "nd"
            : day === 3 || day === 23
              ? "rd"
              : "th";

      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      return `${day}${suffix} ${months[date.getMonth()]} ${date.getFullYear()}`;
    } catch (error) {
      console.error("Error calculating appeal deadline:", error);
      return "";
    }
  };

  const handleEditNextHearingDate = () => {
    setIsEditingNextHearingDate(true);
    const currentDate = caseData?.case.customUndatedRows?.["Next Hearing Date"] ||
      caseData?.case.caseStatus?.["Next Hearing Date"] || "";

    // Parse existing date to YYYY-MM-DD format for date input
    const parsed = parseFancyDate(currentDate);
    if (parsed && !isNaN(parsed.getTime())) {
      // Format as YYYY-MM-DD for date input
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      setNextHearingDateInput(`${year}-${month}-${day}`);
    }
    // else {
    //   // Default to today's date
    //   const today = new Date();
    //   const year = today.getFullYear();
    //   const month = String(today.getMonth() + 1).padStart(2, '0');
    //   const day = String(today.getDate()).padStart(2, '0');
    //   setNextHearingDateInput(`${year}-${month}-${day}`);
    // }
  };

  const handleSaveNextHearingDate = async () => {
    if (!nextHearingDateInput) {
      showToast("Please select a date", "error");
      return;
    }

    try {
      // Convert YYYY-MM-DD to "24th January 2025" format
      const dateObj = new Date(nextHearingDateInput);
      const day = dateObj.getDate();
      const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];
      const month = monthNames[dateObj.getMonth()];
      const year = dateObj.getFullYear();

      const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
      const formattedDate = `${dayWithSuffix} ${month} ${year}`;

      // Determine which API to call based on court type and case type
      const source = caseData?.source?.toLowerCase() || "";

      if (selectedCaseType === 'added') {
        // For added cases, update caseStatus directly
        const payload = {
          caseStatus: {
            ...caseData?.case?.caseStatus,
            "Next Hearing Date": formattedDate
          }
        };

        if (source.includes("high")) {
          await UpdateHighManualCase(courtref, payload);
        } else if (source.includes("district")) {
          await UpdateDistrictManualCase(courtref, payload);
        } else if (source.includes("supreme")) {
          await UpdateSupremeCourtManualCase(courtref, payload);
        } else {
          await UpdateOtherManualCase(courtref, payload);
        }
      } else {
        // For fetched cases, use existing customUndatedRows API
        const payload = {
          "Next Hearing Date": formattedDate
        };

        if (source.includes("high")) {
          await addHcNextHearingDate(courtref, payload);
        } else if (source.includes("district")) {
          await addDcNextHearingDate(courtref, payload);
        } else if (source.includes("supreme")) {
          await UpdateSupremeCourtManualCase(courtref, { customUndatedRows: { "Next Hearing Date": formattedDate } });
        } else {
          await addOcNextHearingDate(courtref, payload);
        }
      }

      // Refresh case data
      const res = await getBothCourtCasesDetailsByCourtRef(courtref);
      setCaseData(res?.data);

      setIsEditingNextHearingDate(false);
      setNextHearingDateInput("");
      showToast("Next hearing date updated successfully!", "success");
    } catch (error) {
      console.error("Error saving next hearing date:", error);
      showToast("Failed to update next hearing date. Please try again.", "error");
    }
  };

  const handleCancelEditNextHearingDate = () => {
    setIsEditingNextHearingDate(false);
    setNextHearingDateInput("");
  };

  const parseAnyDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      // Handle DD-MM-YYYY format (Supreme Court dates like "25-05-2026")
      const ddmmyyyy = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (ddmmyyyy) {
        const d = new Date(`${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`);
        return isNaN(d.getTime()) ? null : d;
      }
      // Remove ordinal suffixes (st, nd, rd, th) for "8th October 2021" style
      const cleaned = dateStr.replace(/(\d+)(st|nd|rd|th)/, "$1");
      const parsed = new Date(cleaned);
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  };

  const isWithin60DaysOfHearing = (hearingDate) => {
    if (!hearingDate) return false;
    try {
      const hearing = parseAnyDate(hearingDate);
      if (!hearing) return false;
      const today = new Date();
      const daysDifference = Math.floor((today - hearing) / (1000 * 60 * 60 * 24));
      return daysDifference <= 60 && daysDifference >= 0;
    } catch (error) {
      console.error("Error checking hearing date:", error);
      return false;
    }
  };

  useEffect(() => {
    if (courtref) {
      showLoading("Loading case details...");
      setHasError(false);
      setPaginationFetched(false); // Reset pagination fetch status

      getBothCourtCasesDetailsByCourtRef(courtref)
        .then((res) => {
          setCaseData(res?.data);

          if (!hasCaseData(res?.data)) {
            setHasError(true);
          } else {
            // Fetch pagination data after case data is loaded
            const source = res?.data?.source;

            if (source && selectedCaseType) {
              // Force fetch on first load to get latest data
              fetchPaginationData(source, selectedCaseType, courtref, true);
            } else {
              console.warn('Missing source or selectedCaseType', { source, selectedCaseType });
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching case details:", err);
          setHasError(true);
        })
        .finally(() => hideLoading());
    }
  }, [courtref]);

  useEffect(() => {
    if (!courtref || fetchedRefs.has(courtref)) return;

    getLabelByCourtRef(courtref)
      .then((res) => {
        setCaseDetailLabels(res.data);
        setFetchedRefs((prev) => new Set(prev).add(courtref));
      })
      .catch((err) => {
        console.error("Error fetching case detail labels:", err);
        setCaseDetailLabels([]);
      });
  }, [courtref, fetchedRefs]);

  useEffect(() => {
    if (!courtref) return;

    refreshAssignments();
  }, [courtref]);

  useEffect(() => {
    if (!courtref) return;

    getClientsByCases(courtref)
      .then((res) => {
        setClients(res?.data || []);
      })
      .catch((err) => {
        console.error("Error fetching clients:", err);
        setClients([]);
      });
  }, [courtref]);

  if (!courtref) return <p>Error: Court reference not provided.</p>;
  if (!caseData) return null;
  if (hasError || !hasCaseData(caseData)) {
    return (
      <div className="case-detail-container">
        <div className="no-data-message">
          <h3>No Data Available</h3>
          <p>No case data is available for this court reference.</p>
        </div>
      </div>
    );
  }

  const CaseStatus = caseData?.case?.caseStatus?.["Case Status"] ||
    caseData?.case?.caseStatus?.["Status"];

  const isCaseDisposed = CaseStatus
    ? ["CASE DISPOSED", "DISPOSED"].includes(CaseStatus.toString().trim().toUpperCase())
    : undefined;

  const appealCheckDate =
    caseData?.case?.caseStatus?.["Decision Date"] ||
    caseData?.case?.customUndatedRows?.["Next Hearing Date"] ||
    caseData?.case?.caseStatus?.["Next Hearing Date"];

  const isAppealCheckDatePast = (() => {
    if (!appealCheckDate) return false;
    const parsed = parseAnyDate(appealCheckDate);
    if (!parsed) return false;
    return parsed <= new Date();
  })();

  const handlePrevCase = () => {
    const { courtRefs, currentIndex } = paginationData;

    if (currentIndex > 0) {
      const prevCourtRef = courtRefs[currentIndex - 1];

      navigate(`/home/cases/casedetails/${prevCourtRef}`, {
        state: {
          selectedCourtType: location.state?.selectedCourtType,
          selectedCaseType: location.state?.selectedCaseType,
        },
        replace: false, // Don't replace history
      });
    }
  };

  const handleNextCase = () => {
    const { courtRefs, currentIndex } = paginationData;

    if (currentIndex < courtRefs.length - 1) {
      const nextCourtRef = courtRefs[currentIndex + 1];

      navigate(`/home/cases/casedetails/${nextCourtRef}`, {
        state: {
          selectedCourtType: location.state?.selectedCourtType,
          selectedCaseType: location.state?.selectedCaseType,
        },
        replace: false, // Don't replace history
      });
    }
  };
  return (
    <div className="case-detail-container">
      <div className="case-detail-header">
        <div className="caseheader-label">
          <div
            className="expandable-case-party-names"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >

            <div
              ref={partyNamesContainerRef}
              style={{
                overflow: isPartyNamesExpanded ? "visible" : "hidden",
                // flex: 1,
                minWidth: 0,
              }}
            >
              <p
                ref={partyNamesContentRef}
                style={{
                  margin: 0,
                  display: "inline",
                  lineHeight: "1.4",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  fontSize: "18px",
                  fontWeight: 'bolder'
                }}
              >
                {isPartyNamesExpanded ? fullPartyNames : truncatedPartyNames}
              </p>
            </div>

            {needsPartyTruncation && (
              <button
                onClick={() => setIsPartyNamesExpanded(!isPartyNamesExpanded)}
                className="case-party-see-more-btn"
                onMouseEnter={(e) => {
                  gsap.to(e.target, {
                    scale: 1.05,
                    backgroundColor: "#004A9A",
                    color: "white",
                    duration: 0.2,
                    ease: "power2.out",
                  });
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.target, {
                    scale: 1,
                    backgroundColor: "transparent",
                    color: "#004A9A",
                    duration: 0.2,
                    ease: "power2.out",
                  });
                }}
              >
                {isPartyNamesExpanded ? "See Less" : "See More"}
              </button>
            )}
            {casedetaillabels.map((label, index) => (
              <span
                key={index}
                className="casedetail-label-pill"
                style={{ backgroundColor: label.color }}
              >
                {label.name}
              </span>
            ))}
          </div>
          {/* {casedetaillabels.map((label, index) => (
            <span
              key={index}
              className="casedetail-label-pill"
              style={{ backgroundColor: label.color }}
            >
              {label.name}
            </span>
          ))} */}

          

          {/* Add Label Button */}
          {role !== "customer" && (
            <button
              className="case-add-label-btn"
              onClick={handleOpenLabelModal}
              title="Add Label"
            >
              {/* <FiPlus size={12} /> Add or Update Labels */}
              Add/Update Labels
            </button>
          )}
          {selectedCaseType === "added" && role !== "customer" && (
            <button className="casedetail-edit-btn" onClick={handleEditCase}>
              ✎ Edit Case
            </button>
          )}
          <button
            className="case-add-label-btn"
            onClick={handleExportPDF}
            title="Export Case Details"
            style={{display: "flex", alignItems: "center", gap: "5px"}}
          >
            <FiDownload size={14} /> Export PDF
          </button>
        </div>
      </div>
      <div className="casedata-details">
        <div className="case-info-grid">
          <div>
            <span className="case-det-grid"><strong>CNR - </strong></span>
            <span className="case-det-grid">{caseData?.case?.caseDetails?.["CNR Number"] || "N/A"}</span>
          </div>

          {/* {caseData.case.caseStatus?.["Next Hearing Date"] ? (
            <div>
              <strong>Next Hearing Date:</strong>{" "}
              {caseData?.case.caseStatus?.["Next Hearing Date"] || "N/A"}
            </div>
          ) : (
            <div>
              <strong>Decision Date:</strong>{" "}
              {caseData?.case.caseStatus?.["Decision Date"] || "N/A"}
            </div>
          )} */}
          <div>
            <span className="case-det-grid"><strong>Stage - </strong></span>
            <span className="case-det-grid">{caseData?.case.caseStatus?.["Stage of Case"] || caseData?.case.caseStatus?.["Case Stage"] || caseData?.case.caseStatus?.["Status/Stage"] || "N/A"}</span>
          </div>
          <div>
            <span className="case-det-grid"><strong>First Hearing Date - </strong></span>
            <span className="case-det-grid">{caseData?.case.caseStatus?.["First Hearing Date"] || "N/A"}</span>
          </div>



          <div>
            <span className="case-det-grid"><strong>File No - </strong></span>
            <span className="case-det-grid">{caseData?.case.caseDetails?.["Filing Number"] || "N/A"}</span>
          </div>
          <div>
            <span className="case-det-grid"><strong>Filing Date - </strong></span>
            <span className="case-det-grid">{caseData?.case.caseDetails?.["Filing Date"] || "N/A"}</span>
          </div>

          <div>
            <span className="case-det-grid"><strong>Created On - </strong></span>
            <span className="case-det-grid">
              {caseData?.case.caseDetails?.["Registration Date"] ||
                caseData?.case.caseDetails?.["Registration Date:"] ||
                "N/A"}
            </span>
          </div>

          <div>
            <span className="case-det-grid"><strong>Status - </strong></span>
            {!isEditingStatus ? (
              <div className="status-display-group">
                <span
                  className={`case-detail-status-${(caseData?.case?.caseStatus?.["Case Status"] || caseData?.case?.caseStatus?.["Status"] || "Pending")
                    .toLowerCase()
                    .replace(/ /g, "-")}`}
                >
                  {caseData?.case?.caseStatus?.["Case Status"] || caseData?.case?.caseStatus?.["Status"] || "Pending"}
                </span>

                {(role !== "customer" &&
                  <button
                    className="status-edit-trigger-btn"
                    onClick={handleEditStatus}
                    title="Edit Status"
                  >
                    <FiEdit />
                  </button>
                )}

              </div>
            ) : (
              <div className="status-edit-container">
                <div className="status-toggle-group">
                  <label className="switch-small">
                    <input
                      type="checkbox"
                      checked={statusInput}
                      onChange={(e) => setStatusInput(e.target.checked)}
                    />
                    <span className="slider-small round"></span>
                  </label>
                  <span className="status-label-text">{statusInput ? "Disposed" : "Pending"}</span>
                </div>

                {statusInput && (
                  <div className="status-date-group">
                    <label>Decision Date:</label>
                    <input
                      type="date"
                      value={decisionDateInput}
                      onChange={(e) => setDecisionDateInput(e.target.value)}
                      className="status-date-input"
                    />
                  </div>
                )}

                <div className="status-actions">
                  <button onClick={handleSaveStatus} className="status-save-btn" title="Save">
                    <FiSave />
                  </button>
                  <button onClick={handleCancelEditStatus} className="status-cancel-btn" title="Cancel">
                    <FiX />
                  </button>
                </div>
              </div>
            )}
          </div>
          <div>
            <span className="case-det-grid"> <strong>District/State - </strong></span>
            <span className="case-det-grid">{resolvedDistrict}/{resolvedState}
            </span>
          </div>
          <div>
            <span className="case-det-grid"><strong>Coram - </strong></span>
            <span className="case-det-grid">{formatText(caseData?.case?.caseStatus?.["Coram"]) || caseData?.case.CaseStatus?.["Court Number and Judge"] || "N/A"}</span>
          </div>
          {(role !== "customer") && (() => {
            const parties = parseClientParties(clients);
            const visible = showAllClients ? parties : parties.slice(0, 3);
            return (
              <div style={{ fontSize: "16px", lineHeight: "1.7em" }}>
                <strong>Clients - </strong>
                {parties.length > 0 ? (
                  <>
                    {visible.map((party, index) => (
                      <span key={party.key}>
                        <span
                          onClick={() => navigate(`/home/my-clients/viewmyclient/${party.clientId}`)}
                          style={{ cursor: "pointer", color: "green", fontWeight: "500" }}
                        >
                          {party.name}
                        </span>
                        {index < visible.length - 1 && ", "}
                      </span>
                    ))}
                    {parties.length > 3 && (
                      <span
                        onClick={() => setShowAllClients(prev => !prev)}
                        style={{ cursor: "pointer", color: "#1a6fcc", fontWeight: "500", fontSize: "0.85rem", marginLeft: "4px" }}
                      >
                        {showAllClients ? "Show less" : `... +${parties.length - 3} more`}
                      </span>
                    )}
                  </>
                ) : "N/A"}
              </div>
            );
          })()}


          {(() => {
            const advocates = parseAdvocates(caseData?.case?.advocates);
            const visible = showAllAdvocates ? advocates : advocates.slice(0, 3);
            return (
              <div style={{ fontSize: "16px", lineHeight: "1.7em" }}>
                <strong>Advocates - </strong>
                {advocates.length > 0 ? (
                  <>
                    {visible.map((name, index) => (
                      <span key={index}>
                        {name}
                        {index < visible.length - 1 && ", "}
                      </span>
                    ))}
                    {advocates.length > 3 && (
                      <span
                        onClick={() => setShowAllAdvocates(prev => !prev)}
                        style={{ cursor: "pointer", color: "#1a6fcc", fontWeight: "500", fontSize: "0.85rem", marginLeft: "4px" }}
                      >
                        {showAllAdvocates ? "Show less" : `... +${advocates.length - 3} more`}
                      </span>
                    )}
                  </>
                ) : "Unassigned"}
              </div>
            );
          })()}


          {role !== "customer" && (
            <div className="office-ref">
              <span className="case-det-grid"><strong>Office Reference - </strong></span>
              <div className="case-office-ref-container">
                {!officeReference && !isEditingOfficeRef ? (
                  <button
                    className="case-add-office-ref-btn"
                    onClick={() => setIsEditingOfficeRef(true)}
                  >
                    <FiPlus size={12} /> Add Office Reference
                  </button>
                ) : isEditingOfficeRef ? (
                  <div className="case-office-ref-edit">
                    <input
                      ref={officeRefInputRef}
                      type="text"
                      value={officeRefInput}
                      maxLength={20}
                      onChange={(e) => setOfficeRefInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOfficeRef();
                        }
                      }}
                      placeholder="Enter office reference"
                      className="case-office-ref-input"
                    />
                    <div className="case-office-ref-actions">
                      <button
                        onClick={handleSaveOfficeRef}
                        className="case-save-icon-btn"
                      >
                        <FiSave size={12} />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="case-cancel-icon-btn"
                      >
                        <FiX size={12} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="case-office-ref-display">
                    <span>{officeReference.officeRefNo}</span>
                    <div className="case-office-ref-actions">
                      <button
                        onClick={handleEditOfficeRef}
                        className="case-edit-icon-btn"
                      >
                        <FiEdit size={12} />
                      </button>
                      <button
                        onClick={handleDeleteOfficeRef}
                        className="case-delete-icon-btn"
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {caseSettings?.allowCustomColumns &&
            caseSettings?.placement === 'case-detail' &&
            role !== 'admin' &&
            role !== 'superAdmin' &&
            caseSettings.columns.map((col, index) => {
              // Find value in existing data
              const valObj = caseData?.case?.customAttributes?.columns?.find(c => c.name === col.name);
              const displayName = col.name.charAt(0).toUpperCase() + col.name.slice(1).replace(/([A-Z])/g, ' $1');

              return (
                <div key={`custom-col-${index}`}>
                  <span className="case-det-grid">
                    <strong>{displayName} - </strong>
                  </span>
                  <span className="case-det-grid">{valObj?.value || "N/A"}</span>
                </div>
              );
            })
          }


        </div>

        <div className="case-sidebar">
          <div className="assignment-info">
            <div className="assignment-header">
              <p>
                <strong>Lawyer's Assigned:</strong>
              </p>
              {assignedUsers.length > 0 && role !== "customer" && (
                <div className="assignment-actions">
                  <button
                    onClick={() => handleOpenAssignmentModal("edit")}
                    className="case-edit-icon-btn"
                    title="Edit assignments"
                  >
                    <FiEdit size={12} />
                  </button>
                  <button
                    onClick={() => handleOpenAssignmentModal("delete")}
                    className="case-delete-icon-btn"
                    title="Manage assignments"
                  >
                    <FiTrash2 size={12} />
                  </button>
                </div>
              )}
            </div>

            <div className="assigned-names">
              {assignedUsers.length > 0 ? (
                assignedUsers.map((user, index) => (
                  <span key={index}>
                    {role !== "customer" ? (
                      <span
                        className="user-link"
                        onClick={() =>
                          (window.location.href = `/home/addusers/${user.id}`)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {user.name}
                      </span>
                    ) : (
                      <span className="user-name-readonly">{user.name}</span>
                    )}
                    {index < assignedUsers.length - 1 && ", "}
                  </span>
                ))
              ) : (
                <div className="unassigned-container">
                  <span>Unassigned</span>
                  {role !== "customer" && (
                    <button
                      onClick={() => handleOpenAssignmentModal("assign")}
                      className="case-add-assignment-btn"
                    >
                      <FiPlus size={12} /> Assign Lawyer
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {!caseData.case.caseStatus?.["Case Status"]?.toLowerCase().includes("dispose") ? (

            <div className="next-hearing-date-section">
              <p>
                <strong>Next Hearing Date:</strong>{" "}
              </p>
              {!isEditingNextHearingDate ? (
                <div className="next-hearing-display">
                  <span className={caseData.case.customUndatedRows?.["Next Hearing Date"] ? "custom-updated-date" : ""}>
                    {caseData.case.customUndatedRows?.["Next Hearing Date"] ||
                      caseData.case.caseStatus?.["Next Hearing Date"] || "N/A"}
                  </span>
                  {role !== "customer" && (selectedCaseType === 'added' || isUndatedNextHearingDate()) && (
                    <button
                      onClick={handleEditNextHearingDate}
                      className="case-edit-icon-btn"
                      title="Edit next hearing date"
                    >
                      <FiEdit size={12} />
                    </button>
                  )}
                </div>
              ) : (
                <div className="next-hearing-edit">
                  <input
                    id="nextHearingDate"
                    type="date"
                    value={nextHearingDateInput}
                    onChange={(e) => setNextHearingDateInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSaveNextHearingDate();
                      }
                    }}
                    className="next-hearing-date-input"
                  />
                  <div className="next-hearing-actions">
                    <button
                      onClick={handleSaveNextHearingDate}
                      className="case-save-icon-btn"
                    >
                      <FiSave size={12} />
                    </button>
                    <button
                      onClick={handleCancelEditNextHearingDate}
                      className="case-cancel-icon-btn"
                    >
                      <FiX size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="casedecison">
              <p>
                <strong>Decision Date:</strong>
              </p>
              <p className="casedetaildate">
                {caseData.case.caseStatus?.["Decision Date"] || "N/A"}
              </p>
            </div>
          )}
        </div>
      </div>

      {
        (role !== "customer" || reAppealData) && isCaseDisposed === false && (isAppealCheckDatePast || reAppealData) && (
          <div className="appeal-info">
            <div className="appeal-data">
              <div className="appeal-header">
                {reAppealData &&
                  isWithin60DaysOfHearing(appealCheckDate) &&
                  role !== "customer" && (
                    <div className="appeal-actions">
                      <button
                        onClick={handleOpenReAppealModal}
                        className="case-edit-icon-btn"
                        title="Edit appeal information"
                      >
                        <FiEdit size={12} />
                      </button>
                      <button
                        onClick={handleDeleteAppeal}
                        className="case-delete-icon-btn"
                        title="Delete appeal information"
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  )}
              </div>

              {!reAppealData ? (
                (() => {
                  if (!appealCheckDate) return null;
                  return isWithin60DaysOfHearing(appealCheckDate) ? (
                    role !== "customer" && (
                      <button
                        className="case-add-appeal-btn"
                        onClick={handleOpenReAppealModal}
                      >
                        <FiPlus size={12} /> Add Appeal Info
                      </button>
                    )
                  ) : (
                    <div className="appeal-time-restriction">
                      <p
                        style={{
                          color: "#666",
                          fontSize: "0.9em",
                          fontStyle: "italic",
                        }}
                      >
                        Appeal period has expired (60 days limit)
                      </p>
                    </div>
                  );
                })()
              ) : reAppealData ? (
                <div className="appeal-display">
                  <div className="appeal-para">
                    <p>
                      <strong>Appeal Period Days:</strong>{" "}
                      {reAppealData.appealPeriodDays || "N/A"}
                    </p>
                    <p>
                      <strong>Appeal Deadline Date:</strong>{" "}
                      {reAppealData.appealDeadlineDate || "N/A"}
                    </p>
                  </div>
                  {(() => {
                    if (!appealCheckDate) return null;
                    return !isWithin60DaysOfHearing(appealCheckDate) ? (
                      <p
                        style={{
                          color: "#666",
                          fontStyle: "italic",
                          marginTop: "3px",
                        }}
                      >
                        (Appeal period has expired - read only)
                      </p>
                    ) : null;
                  })()}
                </div>
              ) : null}
            </div>
            <div className="appeal-information">
              <strong>Appeal Information</strong>
            </div>
          </div>
        )
      }

      <TabViewer
        orderInfo={caseData?.case?.orderInfo || caseData?.case?.orders}
        caseHistory={caseData?.case?.caseHistory}
        documentsInfo={caseData?.case?.documents}
        caseData={caseData}
        selectedCourtType={selectedCourtType}
        selectedCaseType={selectedCaseType}
        courtref={courtref}
        refreshCaseDetailLabels={refreshCaseDetailLabels}
      />

      {/* Assignment Modal */}
      <AssignmentModal
        isOpen={isAssignmentModalOpen}
        onClose={handleCloseAssignmentModal}
        availableUsers={availableUsers}
        assignedUsers={assignedUsers}
        onSave={handleModalSaveAssignments}
        onDeleteUser={handleModalDeleteUser}
        onDeleteAll={handleModalDeleteAll}
        role={role}
        mode={assignmentModalMode}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        itemType={deleteModal.itemType}
        userName={deleteModal.userName}
        onConfirm={
          deleteModal.itemType === "office reference"
            ? confirmDeleteOfficeRef
            : deleteModal.itemType === "appeal information"
              ? confirmDeleteAppeal
              : confirmDeleteAssignments
        }
        onCancel={cancelDelete}
      />

      {/* Re-Appeal Modal */}
      <ReAppealModal
        isOpen={showReAppealModal}
        onClose={handleCloseReAppealModal}
        onSubmit={handleReAppealSubmit}
        decisionDate={
          caseData?.case.caseStatus?.["Decision Date"] ||
          caseData?.case.caseDetails?.["Filing Date"]
        }
        existingReAppealData={reAppealData}
      />
      {/* Label Selection Modal */}
      <LabelSelectionModal
        isOpen={showLabelSelectionModal}
        onClose={handleCloseLabelModal}
        courtRef={courtref}
        onLabelAdded={handleLabelAdded}
        onOpenLabelForm={handleOpenLabelForm}
      />

      {
        showLabelFormModal && (
          <div
            className="label-selection-modal-backdrop"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseLabelForm();
              }
            }}
          >
            <div className="label-form-modal-wrapper">
              <LabelForm
                isModal={true}
                onSuccess={handleLabelFormSuccess}
                onCancel={handleCloseLabelForm}
                initialData={{ courtRef: courtref }}
              />
            </div>
          </div>
        )
      }
    </div >
  );
}

// Re-Appeal Modal Component
const ReAppealModal = ({
  isOpen,
  onClose,
  onSubmit,
  decisionDate,
  existingReAppealData,
}) => {
  const [appealPeriodDays, setAppealPeriodDays] = useState("");
  const [appealDeadlineDate, setAppealDeadlineDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Constants for date dropdowns
  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: 20 }, (_, i) => currentYear + i);

  useEffect(() => {
    if (existingReAppealData && isOpen) {
      // Handle null appealPeriodDays
      setAppealPeriodDays(
        existingReAppealData.appealPeriodDays != null
          ? existingReAppealData.appealPeriodDays.toString()
          : ""
      );
      setAppealDeadlineDate(existingReAppealData.appealDeadlineDate || "");
    } else if (isOpen && !existingReAppealData) {
      setAppealPeriodDays("");
      setAppealDeadlineDate("");
    }
  }, [existingReAppealData, isOpen]);

  // Helper function to parse date in format "20th August 2025"
  const parseCustomDate = (dateString) => {
    if (!dateString) return null;

    try {
      // Remove ordinal suffixes (st, nd, rd, th)
      const cleanedDate = dateString.replace(/(\d+)(st|nd|rd|th)/, "$1");

      // Parse the cleaned date
      const parsedDate = new Date(cleanedDate);

      // Check if date is valid
      if (isNaN(parsedDate.getTime())) {
        console.warn("Invalid date format:", dateString);
        return null;
      }

      return parsedDate;
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return null;
    }
  };

  const ordinalSuffix = (dayNum) => {
    const j = dayNum % 10;
    const k = dayNum % 100;
    if (j === 1 && k !== 11) return dayNum + "st";
    if (j === 2 && k !== 12) return dayNum + "nd";
    if (j === 3 && k !== 13) return dayNum + "rd";
    return dayNum + "th";
  };

  useEffect(() => {
    // Only calculate new date if user is changing days and not using existing data
    if (appealPeriodDays && parseInt(appealPeriodDays) > 0) {
      let baseDate;

      // Use decision date if available, otherwise use current date
      if (decisionDate) {
        baseDate = parseCustomDate(decisionDate);
        if (!baseDate) {
          baseDate = new Date(); // fallback to current date
        }
      } else {
        baseDate = new Date(); // Use current date as fallback
      }

      const deadline = new Date(baseDate);
      deadline.setDate(baseDate.getDate() + parseInt(appealPeriodDays));

      // Format to match your required format "29th August 2026"
      const day = deadline.getDate();

      const formattedDate = `${ordinalSuffix(
        day
      )} ${deadline.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      })}`;

      setAppealDeadlineDate(formattedDate);
    }
  }, [appealPeriodDays, decisionDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Allow submission even if appealPeriodDays is empty (for cases where it might be null)
    if (!appealPeriodDays && !appealDeadlineDate) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        appealPeriodDays: appealPeriodDays ? parseInt(appealPeriodDays) : null,
        appealDeadlineDate,
      });
      setAppealPeriodDays("");
      setAppealDeadlineDate("");
      onClose();
    } catch (error) {
      console.error("Error submitting re-appeal data:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAppealPeriodDays("");
    setAppealDeadlineDate("");
    onClose();
  };

  // --- New Logic for Date Dropdowns ---

  // Parse the deadline date string into parts for the dropdowns
  let parsedDate = { day: "", month: "", year: "" };
  if (appealDeadlineDate) {
    const dateObj = parseCustomDate(appealDeadlineDate);
    if (dateObj && !isNaN(dateObj.getTime())) {
      parsedDate.day = dateObj.getDate();
      parsedDate.month = MONTHS[dateObj.getMonth()];
      parsedDate.year = dateObj.getFullYear();
    }
  }

  // Handle changes from the date dropdowns and update the state
  const handleDateChange = (part, value) => {
    const newDateParts = { ...parsedDate };

    if (part === "day" || part === "year") {
      newDateParts[part] = parseInt(value, 10);
    } else {
      newDateParts[part] = value;
    }

    if (newDateParts.day && newDateParts.month && newDateParts.year) {
      const dayWithSuffix = ordinalSuffix(newDateParts.day);
      const newFormattedDate = `${dayWithSuffix} ${newDateParts.month} ${newDateParts.year}`;
      setAppealDeadlineDate(newFormattedDate);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="reappeal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {existingReAppealData
              ? "Update Re-Appeal Data"
              : "Add Re-Appeal Data"}
          </h3>
          <button type="button" className="close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="appealPeriodDays">Appeal Period (Days):</label>
              <input
                type="number"
                id="appealPeriodDays"
                name="appealPeriodDays"
                value={appealPeriodDays}
                onChange={(e) => setAppealPeriodDays(e.target.value)}
                min="1"
                max="365"
                required
                placeholder="Enter number of days"
                autoFocus
              />
              {decisionDate && (
                <small
                  style={{
                    color: "#6b7280",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  Decision Date: {decisionDate}
                </small>
              )}
            </div>

            {appealDeadlineDate && (
              <div className="form-group">
                <label>Appeal Deadline Date:</label>
                <div className="date-select-container">
                  {/* Day Dropdown */}
                  <select
                    name="day"
                    className="date-select"
                    value={parsedDate.day}
                    onChange={(e) => handleDateChange("day", e.target.value)}
                  >
                    <option value="" disabled>
                      Day
                    </option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>

                  {/* Month Dropdown */}
                  <select
                    name="month"
                    className="date-select"
                    value={parsedDate.month}
                    onChange={(e) => handleDateChange("month", e.target.value)}
                  >
                    <option value="" disabled>
                      Month
                    </option>
                    {MONTHS.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>

                  {/* Year Dropdown */}
                  <select
                    name="year"
                    className="date-select"
                    value={parsedDate.year}
                    onChange={(e) => handleDateChange("year", e.target.value)}
                  >
                    <option value="" disabled>
                      Year
                    </option>
                    {YEARS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="calculated-date-display">
                  Selected Date: <strong>{appealDeadlineDate}</strong>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                onClick={handleClose}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  (!appealPeriodDays && !appealDeadlineDate) || isSubmitting
                }
                className="submit-btn"
              >
                {isSubmitting
                  ? existingReAppealData
                    ? "Updating..."
                    : "Adding..."
                  : existingReAppealData
                    ? "Update Re-Appeal Data"
                    : "Add Re-Appeal Data"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
